import React, { useEffect } from "react";
import styles from "./template-results.module.scss";
import { useLabFiltering } from "@hub/services/use-lab-filtering";
import Image from "next/image";
import Link from "next/link";
import { LabCardData } from "@hub/models/document/document-card-data";
import { DexLabsSearchBarComponent } from "@hub/ui/filtering/dex-labs-search-bar/dex-labs-search-bar.component";
import { useRouter } from "next/router";
import { LabFilteringService } from "@hub/services/filtering/lab-filtering.service";

interface TemplateListComponentProps {
  items: LabCardData[];
  topic: string;
}

export const TemplateListComponent: React.FC<TemplateListComponentProps> = ({
  items,
  topic,
}) => {
  const searchBarMessage = `Search ${topic}`;

  const { isReady, query } = useRouter();
  const { results } = useLabFiltering();

  const filterQuery = query as {
    topic: string;
    searchTerm: string;
  };

  useEffect(() => {
    if (isReady && items) {
      const enabledFilters: {
        topic: boolean;
      } = {
        topic: true,
      };

      LabFilteringService.setEnabledFilterGroups(enabledFilters);
      LabFilteringService.setFilterOptions(null);
      LabFilteringService.initializeFilterGroups({
        query: filterQuery,
        contentCardData: items,
      });
    }
  }, [filterQuery, isReady, items]);

  if (!results) {
    return <div className={styles.container}>No results.</div>;
  }

  return (
    <div className={styles.container}>
      <DexLabsSearchBarComponent placeholderMessage={searchBarMessage} />
      {results.length === 0 && (
        <div className={styles.container}>No results match your criteria.</div>
      )}
      {results.length > 0 && (
        <div className={styles.results}>
          {results.map((result) => {
            if (!result.thumbnail) {
              return null;
            }

            return (
              <Link
                href={result.path}
                className={styles.result}
                key={result.path}
              >
                <div className={styles.result__image}>
                  <Image
                    src={result.thumbnail}
                    alt={"test"}
                    fill
                    style={{
                      objectFit: "contain",
                    }}
                  />
                </div>
                <div className={styles.result__content}>
                  <h3 className={styles.result__title}>{result.title}</h3>
                  <span className={styles.result__description}>
                    {result.description}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface TemplateResultsComponentProps {
  searchBarMessage?: string;
  topic: string;
  items: LabCardData[];
}

export const TemplateResultsComponent: React.FC<
  TemplateResultsComponentProps
> = ({ topic, items }) => {
  return <TemplateListComponent topic={topic} items={items} />;
};
